import React from 'react';
import { ContentfulTheme } from '@jbhi-fi/apis/src/contentful/types';
import { DeepPartial } from '@jbhi-fi/utils/src/DeepPartial';
import { Icon, IconName } from '@jbhi-fi/lanyard-ui/components/Icon';
import { HeadingFontVariant } from '../../HeadingDivider/HeadingFont';

export type ContentThemePalette = {
  background?: string;
  text?: string;
  heading?: string;
  headingBackground?: string;
  backgroundImage?: string;
  headingFontVariant?: HeadingFontVariant;
};

export type ContentTheme = {
  palette: ContentThemePalette;
  icon?: IconName;
};

export type LegacyContentTheme = {
  backgroundColor: string;
  textColor: string;
  icon?: React.ReactNode;
};

export abstract class ThemeTransformer {
  static stringToColorValue = (str: string): string => {
    if (str?.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i)) {
      return `#${str.replace(/#/g, '').toUpperCase()}`;
    }
    return str;
  };

  static toContentThemePalette = (entry?: ContentfulTheme): DeepPartial<ContentThemePalette> => {
    return entry?.fields
      ? {
          background: entry.fields.backgroundColourHex
            ? this.stringToColorValue(entry.fields.backgroundColourHex)
            : undefined,
          text: entry.fields.textColourHex ? this.stringToColorValue(entry?.fields?.textColourHex) : undefined,
          heading: entry.fields.headingColourHex ? this.stringToColorValue(entry.fields.headingColourHex) : undefined,
          headingBackground: entry.fields.headingBackgroundColourHex
            ? this.stringToColorValue(entry?.fields?.headingBackgroundColourHex)
            : undefined,
          backgroundImage: entry.fields.backgroundImage?.fields?.file?.url ?? undefined,
          headingFontVariant: entry.fields.headingFont ?? undefined,
        }
      : {};
  };
  static toContentThemeVariables = (entry?: ContentfulTheme): DeepPartial<ContentTheme> => {
    return entry?.fields
      ? {
          palette: this.toContentThemePalette(entry),
          icon: entry?.fields?.icon?.fields?.name as IconName,
        }
      : { palette: {} };
  };

  static toLegacyContentThemeProps = (entry?: ContentfulTheme): LegacyContentTheme => {
    const theme = this.toContentThemePalette(entry);

    return {
      backgroundColor: theme.background ?? '#232323',
      textColor: theme.text ?? '#FFF',
      icon: entry?.fields?.icon?.fields ? <Icon name={entry.fields.icon.fields.name} /> : null,
    };
  };
}
